import type { SupportLevel } from '../../core/types.js';
import type { ValidatedConfig } from '../../config/core/schema.js';
import {
  shouldConvertAgentsToSkills,
  shouldConvertCommandsToSkills,
} from '../../config/core/conversions.js';
import type {
  FeatureGeneratorFn,
  TargetCapabilities,
  TargetGenerators,
} from './target.interface.js';
import {
  type CapabilityFeatureKey,
  normalizeTargetCapabilities,
  type TargetCapabilityValue,
} from './capabilities.js';
import type {
  TargetDescriptor,
  TargetLayout,
  TargetLayoutScope,
  TargetManagedOutputs,
} from './target-descriptor.js';
import { getDescriptor } from './registry.js';
import { assertSharedArtifactOwnersUnique } from './shared-artifact-owner.js';
import { TARGET_IDS, type BuiltinTargetId, isBuiltinTargetId } from './target-ids.js';
// <auto-generated:builtin-target-imports>
import { descriptor as aider } from '../aider/index.js';
import { descriptor as amazonQ } from '../amazon-q/index.js';
import { descriptor as amp } from '../amp/index.js';
import { descriptor as antigravity } from '../antigravity/index.js';
import { descriptor as augmentCode } from '../augment-code/index.js';
import { descriptor as claudeCode } from '../claude-code/index.js';
import { descriptor as cline } from '../cline/index.js';
import { descriptor as codexCli } from '../codex-cli/index.js';
import { descriptor as continueTarget } from '../continue/index.js';
import { descriptor as copilot } from '../copilot/index.js';
import { descriptor as crush } from '../crush/index.js';
import { descriptor as cursor } from '../cursor/index.js';
import { descriptor as deepagentsCli } from '../deepagents-cli/index.js';
import { descriptor as factoryDroid } from '../factory-droid/index.js';
import { descriptor as geminiCli } from '../gemini-cli/index.js';
import { descriptor as goose } from '../goose/index.js';
import { descriptor as jules } from '../jules/index.js';
import { descriptor as junie } from '../junie/index.js';
import { descriptor as kiloCode } from '../kilo-code/index.js';
import { descriptor as kiro } from '../kiro/index.js';
import { descriptor as opencode } from '../opencode/index.js';
import { descriptor as piAgent } from '../pi-agent/index.js';
import { descriptor as qwenCode } from '../qwen-code/index.js';
import { descriptor as replitAgent } from '../replit-agent/index.js';
import { descriptor as rooCode } from '../roo-code/index.js';
import { descriptor as rovodev } from '../rovodev/index.js';
import { descriptor as trae } from '../trae/index.js';
import { descriptor as warp } from '../warp/index.js';
import { descriptor as windsurf } from '../windsurf/index.js';
import { descriptor as zed } from '../zed/index.js';
// </auto-generated:builtin-target-imports>

type TargetFeature = keyof TargetCapabilities;

/** Capabilities for a target that has no support in a given scope. */
const ALL_NONE_CAPABILITIES: TargetCapabilities = {
  rules: 'none',
  additionalRules: 'none',
  commands: 'none',
  agents: 'none',
  skills: 'none',
  mcp: 'none',
  hooks: 'none',
  ignore: 'none',
  permissions: 'none',
};

/** @deprecated Use TargetDescriptor from target-descriptor.ts instead */
export type BuiltinTargetDefinition = TargetDescriptor;

/**
 * Builtin target descriptors. The imports above and the array below are kept
 * in sync with `src/targets/<id>/index.ts` by `scripts/generate-target-catalog.ts`
 * (run via `pnpm catalog:generate` or the `prebuild` hook). To add or remove a
 * target, drop or delete the directory under `src/targets/` and rerun the script.
 * Order: alphabetical by directory name.
 */
// <auto-generated:builtin-targets-array>
export const BUILTIN_TARGETS: readonly TargetDescriptor[] = [
  aider,
  amazonQ,
  amp,
  antigravity,
  augmentCode,
  claudeCode,
  cline,
  codexCli,
  continueTarget,
  copilot,
  crush,
  cursor,
  deepagentsCli,
  factoryDroid,
  geminiCli,
  goose,
  jules,
  junie,
  kiloCode,
  kiro,
  opencode,
  piAgent,
  qwenCode,
  replitAgent,
  rooCode,
  rovodev,
  trae,
  warp,
  windsurf,
  zed,
];
// </auto-generated:builtin-targets-array>

// Lazily initialized to avoid circular-dependency issues during module load.
let _builtinTargetsMap: Map<string, TargetDescriptor> | undefined;
function builtinTargetsMap(): Map<string, TargetDescriptor> {
  if (!_builtinTargetsMap) {
    // Fail fast if two builtins claim the same/overlapping shared-artifact owner
    // prefix — would otherwise silently depend on iteration order in the rewriter.
    assertSharedArtifactOwnersUnique(BUILTIN_TARGETS);
    _builtinTargetsMap = new Map(BUILTIN_TARGETS.map((d) => [d.id, d]));
  }
  return _builtinTargetsMap;
}

// Re-export from target-ids.ts for backward compatibility
export { TARGET_IDS, type BuiltinTargetId, isBuiltinTargetId };

export function getBuiltinTargetDefinition(target: string): TargetDescriptor | undefined {
  return builtinTargetsMap().get(target);
}

export function getTargetCapabilities(
  target: string,
  scope: TargetLayoutScope = 'project',
): Record<CapabilityFeatureKey, TargetCapabilityValue> | undefined {
  const descriptor = getBuiltinTargetDefinition(target) ?? getDescriptor(target);
  if (!descriptor) return undefined;
  if (scope === 'global' && !descriptor.globalSupport) {
    // A target with no global mode (e.g. cloud-only jules/replit-agent) must
    // report no support in global scope — never fall back to project
    // capabilities, or the matrix/docs would claim support that `generate
    // --global` cannot satisfy (no globalSupport.layout => zero files).
    return normalizeTargetCapabilities(ALL_NONE_CAPABILITIES);
  }
  const raw = scope === 'global' ? descriptor.globalSupport!.capabilities : descriptor.capabilities;
  return normalizeTargetCapabilities(raw);
}

export function getTargetDetectionPaths(
  target: string,
  scope: TargetLayoutScope = 'project',
): readonly string[] {
  const descriptor = getBuiltinTargetDefinition(target) ?? getDescriptor(target);
  if (!descriptor) return [];
  if (scope === 'global') {
    return descriptor.globalSupport?.detectionPaths ?? [];
  }
  return descriptor.detectionPaths;
}

export function getTargetLayout(
  target: string,
  scope: TargetLayoutScope = 'project',
): TargetLayout | undefined {
  const descriptor = getBuiltinTargetDefinition(target) ?? getDescriptor(target);
  if (!descriptor) return undefined;
  if (scope === 'global') {
    return descriptor.globalSupport?.layout;
  }
  return descriptor.project;
}

export function getTargetPrimaryRootInstructionPath(
  target: string,
  scope: TargetLayoutScope = 'project',
): string | undefined {
  return getTargetLayout(target, scope)?.rootInstructionPath;
}

export function getTargetSkillDir(
  target: string,
  scope: TargetLayoutScope = 'project',
): string | undefined {
  return getTargetLayout(target, scope)?.skillDir;
}

export function getTargetManagedOutputs(
  target: string,
  scope: TargetLayoutScope = 'project',
): TargetManagedOutputs | undefined {
  return getTargetLayout(target, scope)?.managedOutputs;
}

export function rewriteGeneratedOutputPath(
  target: string,
  path: string,
  scope: TargetLayoutScope = 'project',
): string | null {
  const layout = getTargetLayout(target, scope);
  if (!layout) return null;
  return layout.rewriteGeneratedPath ? layout.rewriteGeneratedPath(path) : path;
}

/**
 * Single source of truth for the descriptor-level conversion guard
 * (`supportsConversion`). When a target declares it can fall back to a
 * skill projection for commands/agents, we let the user disable that
 * projection via config; the result is observed identically by the
 * matrix display (effective support level) and the generate dispatch
 * (feature generator lookup), so both must read this helper.
 */
function isFeatureSuppressedByConversion(
  descriptor: TargetDescriptor | undefined,
  feature: keyof TargetCapabilities,
  config: ValidatedConfig | undefined,
  scope: TargetLayoutScope,
): boolean {
  if (!descriptor || !config) return false;
  const baseLevel = getTargetCapabilities(descriptor.id, scope)?.[feature]?.level ?? 'none';
  if (baseLevel === 'native' || baseLevel === 'partial') return false;
  if (feature === 'commands' && descriptor.supportsConversion?.commands) {
    return !shouldConvertCommandsToSkills(config, descriptor.id, true, scope);
  }
  if (feature === 'agents' && descriptor.supportsConversion?.agents) {
    return !shouldConvertAgentsToSkills(config, descriptor.id, true, scope);
  }
  return false;
}

function isConversionUpgrading(
  descriptor: TargetDescriptor | undefined,
  feature: keyof TargetCapabilities,
  config: ValidatedConfig | undefined,
  scope: TargetLayoutScope,
): boolean {
  if (!descriptor || !config) return false;
  if (feature === 'commands' && descriptor.supportsConversion?.commands) {
    return shouldConvertCommandsToSkills(config, descriptor.id, true, scope);
  }
  if (feature === 'agents' && descriptor.supportsConversion?.agents) {
    return shouldConvertAgentsToSkills(config, descriptor.id, true, scope);
  }
  return false;
}

export function getEffectiveTargetSupportLevel(
  target: string,
  feature: keyof TargetCapabilities,
  config: ValidatedConfig,
  scope: TargetLayoutScope = 'project',
): SupportLevel {
  const baseLevel = getTargetCapabilities(target, scope)?.[feature]?.level ?? 'none';
  const descriptor = getBuiltinTargetDefinition(target) ?? getDescriptor(target);
  // No global mode => no scope to project skills into; a conversion upgrade
  // would falsely report `embedded` while `generate --global` emits nothing.
  if (scope === 'global' && descriptor && !descriptor.globalSupport) return 'none';
  if (baseLevel === 'none' && isConversionUpgrading(descriptor, feature, config, scope)) {
    return 'embedded';
  }
  if (baseLevel !== 'embedded') return baseLevel;
  return isFeatureSuppressedByConversion(descriptor, feature, config, scope) ? 'none' : baseLevel;
}

type FeatureGeneratorPick = (g: TargetGenerators) => FeatureGeneratorFn | undefined;

const PICK_FEATURE_GENERATOR: Record<TargetFeature, FeatureGeneratorPick | null> = {
  rules: (g) => g.generateRules,
  additionalRules: null,
  commands: (g) => g.generateCommands,
  agents: (g) => g.generateAgents,
  skills: (g) => g.generateSkills,
  mcp: (g) => g.generateMcp,
  permissions: (g) => g.generatePermissions,
  hooks: (g) => g.generateHooks,
  ignore: (g) => g.generateIgnore,
};

export function resolveTargetFeatureGenerator(
  target: string,
  feature: TargetFeature,
  config?: ValidatedConfig,
  scope: TargetLayoutScope = 'project',
): FeatureGeneratorFn | undefined {
  const descriptor = getBuiltinTargetDefinition(target) ?? getDescriptor(target);
  if (!descriptor?.generators) return undefined;
  if (isFeatureSuppressedByConversion(descriptor, feature, config, scope)) return undefined;
  const pick = PICK_FEATURE_GENERATOR[feature];
  return pick === null ? undefined : pick(descriptor.generators);
}
